import { useState } from 'react';
import type { ExpenseDTO, SaveExpenseRequest } from '@six-pockets/shared';
import { DatePicker, MoneyInput, SegmentedControl, TextField } from '@/components/ui';
import { FormRow, ManageFormModal } from '@/features/manage/components/ManageFormModal';
import { parseWhole } from '@/features/manage/draft';
import { todayKey } from '@/utils/date';
import { SOURCE_OPTIONS, type PickableSource } from '../sources';

interface ExpenseFormModalProps {
  /** Null to add an expense. */
  expense: ExpenseDTO | null;
  busy: boolean;
  error: string | null;
  onSave: (body: SaveExpenseRequest) => void;
  onClose: () => void;
}

/**
 * Add or edit an expense: amount, reason and who paid (source). Adding also takes the day it was spent (today unless
 * changed: staff back from leave enter the days they missed). Mount it only while open.
 */
export function ExpenseFormModal({ expense, busy, error, onSave, onClose }: ExpenseFormModalProps) {
  const [amount, setAmount] = useState(expense ? String(expense.amount) : '');
  const [reason, setReason] = useState(expense?.reason ?? '');
  // A waived balance is never edited here (the row has no ✎), so 'waiver' never reaches the form.
  const [source, setSource] = useState<PickableSource>(expense && expense.source !== 'waiver' ? expense.source : 'counter');
  // Adding only: the day it is recorded on, sent when it isn't today.
  const [today] = useState(todayKey);
  const [date, setDate] = useState(today);

  const value = parseWhole(amount);
  const blocked = !value ? 'Enter the amount in whole rupees' : !reason.trim() ? 'Enter a reason' : undefined;

  return (
    <ManageFormModal
      title={expense ? 'Edit expense' : 'Add expense'}
      submitLabel={expense ? 'Save changes' : 'Add expense'}
      busy={busy}
      error={error}
      blockedReason={blocked}
      onSubmit={() => onSave({ amount: value!, reason: reason.trim(), source, ...(!expense && date !== today && { date }) })}
      onClose={onClose}
    >
      <FormRow>
        <MoneyInput label="Amount" value={amount} onChange={setAmount} data-autofocus />
        <TextField label="Reason" placeholder="Staff salaries" value={reason} maxLength={120} onChange={(e) => setReason(e.target.value)} />
      </FormRow>
      {!expense && <DatePicker value={date} onChange={setDate} label="Day spent" fieldLabel="Day" inline />}
      {/* One-tap buttons, as in the bill modal (a dropdown would be clipped by the modal body). */}
      <SegmentedControl legend="Paid from" options={SOURCE_OPTIONS} value={source} onChange={setSource} />
    </ManageFormModal>
  );
}
